import { Injectable } from '@angular/core';
import * as L from 'leaflet';
import 'leaflet.markercluster';
import 'leaflet-search';

export type TileProvider =
    | 'mapBoxStreet'
    | 'mapBoxSatellite'
    | 'openStreetMap'
    | 'mapTilerStreet'
    | 'mapTilerSatellite';

@Injectable({
    providedIn: 'root',
})
export class TileService {
    load(
        map: L.Map,
        layersControl: L.Control.Layers,
        defaultTiles: TileProvider,
        tokens?: Partial<Record<TileProvider, string>>
    ) {
        if (!map) {
            throw new Error('A map has not been created yet. Can not load tiles.');
        }
        const tileProviders: Partial<Record<TileProvider, any>> = {
            openStreetMap: L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                maxZoom: 19,
                minZoom: 3,
                attribution: '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>',
            }),
        };
        if (tokens && tokens.mapBoxStreet) {
            tileProviders.mapBoxStreet = L.tileLayer(
                'https://api.mapbox.com/styles/v1/{id}/tiles/{z}/{x}/{y}?access_token={accessToken}',
                {
                    attribution:
                        'Map data &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, Imagery © <a href="https://www.mapbox.com/">Mapbox</a>',
                    maxZoom: 21,
                    minZoom: 3,
                    id: 'mapbox/streets-v11',
                    accessToken: tokens.mapBoxStreet,
                }
            );
            tileProviders.mapBoxSatellite = L.tileLayer(
                'https://api.mapbox.com/styles/v1/{id}/tiles/{z}/{x}/{y}?access_token={accessToken}',
                {
                    attribution:
                        'Map data &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, Imagery © <a href="https://www.mapbox.com/">Mapbox</a>',
                    maxZoom: 21,
                    minZoom: 3,
                    id: 'mapbox/satellite-v9',
                    accessToken: tokens.mapBoxSatellite,
                }
            );
        }
        if (tokens && tokens.mapTilerStreet) {
            tileProviders.mapTilerStreet = L.tileLayer(
                'https://api.maptiler.com/maps/streets-v2/256/{z}/{x}/{y}.png?key={accessToken}',
                {
                    attribution:
                        'Map data &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, Imagery © <a href="https://www.maptiler.com/">Maptiler</a>',
                    maxZoom: 22,
                    minZoom: 1,
                    id: 'maptiler/streets',
                    accessToken: tokens.mapTilerStreet,
                }
            );
            tileProviders.mapTilerSatellite = L.tileLayer(
                'https://api.maptiler.com/maps/hybrid/256/{z}/{x}/{y}.jpg?key={accessToken}',
                {
                    attribution:
                        'Map data &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, Imagery © <a href="https://www.maptiler.com/">Maptiler</a>',
                    maxZoom: 22,
                    minZoom: 1,
                    id: 'maptiler/hybrid',
                    accessToken: tokens.mapTilerSatellite,
                }
            );
        }

        if (!tileProviders[defaultTiles]) {
            throw new Error(
                `Missing requested default map tile provider ${defaultTiles}. Check that the provider name is not misspelled. Also, did you provide a token for it?`
            );
        }
        tileProviders[defaultTiles].addTo(map);

        if (tileProviders.mapBoxStreet) {
            layersControl.addBaseLayer(tileProviders.mapBoxStreet, 'MapBox streets');
        }
        if (tileProviders.mapBoxSatellite) {
            layersControl.addBaseLayer(tileProviders.mapBoxSatellite, 'MapBox satellite');
        }
        if (tileProviders.mapTilerStreet) {
            layersControl.addBaseLayer(tileProviders.mapTilerStreet, 'Map');
        }
        if (tileProviders.mapTilerSatellite) {
            layersControl.addBaseLayer(tileProviders.mapTilerSatellite, 'Satellite');
        }
    }
}
